import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookMarked, Search } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useCommentaries, CommentaryPicker } from "@/components/commentary/CommentaryPanel";
import { CommentarySearchBox } from "./CommentarySearch";
import { CommentaryAccessNotice, isAccessError } from "@/components/commentary/CommentaryAccess";
import { ReportCorrection } from "@/components/ReportCorrection";

function Hub({ list }) {
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="commentary-hub">
      <Seo title="Commentaries" description="Read classic Bible commentaries side by side with Scripture." />
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Commentaries</span>
      <h1 className="mt-2 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">Read alongside the saints</h1>
      <p className="mt-3 max-w-2xl text-base text-muted-foreground">Classic commentaries you can search on their own or open beside any chapter in the Bible Reader. Highlight and journal from them just as you do in Scripture, and see how faithful teachers wrestled with the same questions you're asking.</p>
      <p className="mt-3 max-w-2xl text-sm italic text-muted-foreground" data-testid="commentary-hub-caveat">These are trusted human voices, not Scripture itself. Weigh what they say against the Word.</p>
      {list.length === 0 && <p className="mt-8 rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="commentary-hub-empty">No commentaries are available right now.</p>}
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {list.map((c) => (
          <Link key={c.slug} to={`/commentary/${c.slug}`} className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-gold hover:shadow-md" data-testid={`commentary-card-${c.slug}`}>
            <BookMarked className="h-5 w-5 text-gold" />
            <h2 className="mt-3 font-serif text-2xl font-semibold text-foreground group-hover:text-primary" data-testid={`commentary-card-name-${c.slug}`}>{c.display_name || c.name}</h2>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{c.author} · {c.year} · {c.chapters.toLocaleString()} chapters</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.description}</p>
          </Link>
        ))}
      </div>
      <div className="mt-10 space-y-2 text-sm text-muted-foreground">
        <p data-testid="commentary-hub-references">Looking for Keil &amp; Delitzsch, Albert Barnes, or the lexicons behind our word studies? See <Link to="/references" className="text-primary hover:underline">References &amp; Sources</Link>.</p>
        <p className="flex items-center gap-1">Spot something that needs fixing? <ReportCorrection reference="Commentaries page" testId="commentary-hub-report-correction" /></p>
      </div>
    </div>
  );
}

/* /commentary → hub (or straight to the only commentary) · /commentary/:slug → book grid + search */
export default function CommentaryHome() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: list, isLoading } = useCommentaries();
  const { data, error } = useQuery({ queryKey: ["commentary-books", slug], queryFn: async () => (await api.get(`/commentaries/${slug}`)).data, enabled: !!slug, staleTime: 60 * 60 * 1000, retry: (n, e) => !isAccessError(e) && n < 2 });
  if (isLoading) return <div className="mx-auto max-w-5xl px-4 py-10"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  if (!slug) return list?.length === 1 ? <Navigate to={`/commentary/${list[0].slug}`} replace /> : <Hub list={list || []} />;
  if (error) return <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8"><Seo title="Commentary" /><CommentaryAccessNotice error={error} /></div>;
  if (!data) return <div className="mx-auto max-w-5xl px-4 py-10"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  const ot = data.books.filter((b) => b.testament === "OT"), nt = data.books.filter((b) => b.testament === "NT");
  const Grid = ({ title, rows }) => (
    <section>
      <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{title}</h2>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {rows.map((b) => (
          <Link key={b.book} to={`/commentary/${slug}/${encodeURIComponent(b.book)}`} className="flex items-baseline justify-between rounded-xl border border-border bg-card px-3 py-2.5 transition-colors hover:border-gold" data-testid={`commentary-book-${b.book.replace(/\s+/g, "-")}`}>
            <span className="font-serif text-[15px] text-foreground">{b.book}</span>
            <span className="font-mono text-[10px] text-muted-foreground">{b.chapters} ch</span>
          </Link>
        ))}
      </div>
    </section>
  );
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="commentary-home">
      <Seo title={`${data.name} Commentary`} description={data.description} />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <Link to="/commentary" className="font-mono text-xs uppercase tracking-[0.25em] text-gold hover:underline">Commentaries</Link>
          <h1 className="mt-2 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl" data-testid="commentary-title">{data.full_name}</h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{data.author} · {data.year}</p>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground" data-testid="commentary-description">{data.detail || data.description}</p>
        </div>
        {list?.length > 1 && (
          <div className="flex shrink-0 flex-col gap-1.5 sm:items-end sm:pt-1" data-testid="commentary-switch">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Switch commentary</span>
            <CommentaryPicker slug={slug} onChange={(v) => navigate(`/commentary/${v}`)} testId="commentary-switch-select" />
          </div>
        )}
      </div>
      <div className="mt-8"><CommentarySearchBox slug={slug} onSubmit={(q) => navigate(`/commentary/${slug}/search?q=${encodeURIComponent(q)}`)} /></div>
      <div className="mt-10 space-y-10">
        <Grid title="Old Testament" rows={ot} />
        <Grid title="New Testament" rows={nt} />
      </div>
      <p className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground"><Search className="h-4 w-4 text-gold" /> Tip: open any chapter in the <Link to="/read" className="text-primary hover:underline">Bible Reader</Link> and press “Show commentary” to read them side by side.</p>
    </div>
  );
}
